import {
  useEffect,
  useId,
  useRef,
  type FormEvent,
  type ReactNode,
} from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S412 — the frame the account Actions menu's dialogs share (send an
// application, request a rider, report a claim): a title, a lead line, the
// form, the server's refusal inline above the buttons, and Cancel beside the
// send.
//
// NO OPTIMISTIC SUCCESS (NN #7). `success` is set by the caller only from
// PMS's 2xx; until then the form stays, with what was typed. Once it is set
// the form gives way to that line and a Done button, so the same request is
// never sent twice by a second click.
//
// `maxWidth` widens it for a form that carries the wizard's two-column grid
// (the rider's bond details, BRK-S420).
//
// FOCUS ON DONE (BRK-S489). The 2xx removes the focused send button; left
// alone, focus falls to <body>, outside the dialog, and Escape stops closing
// it. Done takes focus as it appears, so the keyboard keeps its place.
//
// FULL SCREEN ON A PHONE for every caller (BRK-S493), as the v2 dialogs are.

export function ActionFormDialog({
  open,
  onClose,
  title,
  lead,
  submitLabel,
  busyLabel = "Sending…",
  busy,
  error,
  success,
  canSubmit = true,
  onSubmit,
  maxWidth = "sm",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  lead?: ReactNode;
  submitLabel: string;
  busyLabel?: string;
  busy: boolean;
  /** The refusal's line, or null. */
  error: string | null;
  /** The confirmation, set only from the server's 2xx; null until then. */
  success: string | null;
  /** False when there is nothing the form could send (no bond to pick). */
  canSubmit?: boolean;
  onSubmit: () => void;
  maxWidth?: "sm" | "md";
  children: ReactNode;
}) {
  const titleId = useId();
  const formId = useId();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const doneRef = useRef<HTMLButtonElement>(null);
  const confirmed = success !== null;

  // After the dialog's focus trap has settled on the swap: `autoFocus` alone
  // loses to the trap pulling focus back to its container.
  useEffect(() => {
    if (confirmed) doneRef.current?.focus();
  }, [confirmed]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!busy) onSubmit();
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby={titleId}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={phone}
      scroll="paper"
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        {success !== null ? (
          <p
            role="status"
            className="text-sm text-green-700 dark:text-green-300"
          >
            <span aria-hidden="true">✓ </span>
            <span>{success}</span>
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {lead && <p className="text-sm text-brk-dim">{lead}</p>}
            {/* The browser's own required-field bubbles are off: the dialog
                says what is missing in its own words, under each field. */}
            <form
              id={formId}
              noValidate
              onSubmit={submit}
              className="flex flex-col gap-3"
            >
              {children}
            </form>
            {error && (
              <p role="alert" className="text-sm text-brk-danger">
                {error}
              </p>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {/* Stacked at 375 (the send on top), in a row from `sm:`. */}
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {success !== null ? (
            <button
              type="button"
              ref={doneRef}
              onClick={onClose}
              className={buttonClass()}
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className={buttonClass({ variant: "secondary" })}
              >
                Cancel
              </button>
              {canSubmit && (
                <button
                  type="submit"
                  form={formId}
                  disabled={busy}
                  className={buttonClass()}
                >
                  {busy ? busyLabel : submitLabel}
                </button>
              )}
            </>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
}
